import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useSocket } from '../contexts/SocketContext';
import toast from 'react-hot-toast';

const Chat = () => {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [agents, setAgents] = useState([]);
  const messagesEndRef = useRef(null);
  const socket = useSocket();

  useEffect(() => {
    fetchChats();
    fetchAgents();
  }, []);

  useEffect(() => {
    if (activeChat) {
      fetchMessages(activeChat);
    }
  }, [activeChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChats = async () => {
    try {
      const res = await axios.get('/api/chat');
      setChats(res.data.chats);
      if (res.data.chats.length > 0 && !activeChat) {
        setActiveChat(res.data.chats[0]._id);
      }
    } catch (error) {
      toast.error('Failed to load chats');
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await axios.get('/api/agents');
      setAgents(res.data.agents);
    } catch (error) {
      console.error('Failed to load agents');
    }
  };

  const fetchMessages = async (chatId) => {
    try {
      const res = await axios.get(`/api/chat/${chatId}/messages`);
      setMessages(res.data.messages);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat) return;

    try {
      // Extract mentions
      const mentions = [];
      agents.forEach(agent => {
        if (newMessage.includes(`@${agent.name}`)) {
          mentions.push({ type: 'agent', id: agent._id });
        }
      });

      await axios.post(`/api/chat/${activeChat}/messages`, {
        content: newMessage,
        mentions
      });

      setNewMessage('');
      fetchMessages(activeChat);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const activeChatData = chats.find(c => c._id === activeChat);

  return (
    <div className="h-[calc(100vh-140px)] flex gap-6">
      {/* Chat List */}
      <div className="w-80 bg-[#12121a] border border-[#2d2d3d] rounded-2xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-[#2d2d3d]">
          <h3 className="font-semibold">Conversations</h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          {chats.map((chat) => (
            <button
              key={chat._id}
              onClick={() => setActiveChat(chat._id)}
              className={`w-full p-4 flex items-center gap-3 hover:bg-[#1e1e2e] transition-colors border-l-2 ${
                activeChat === chat._id
                  ? 'border-indigo-500 bg-indigo-500/5'
                  : 'border-transparent'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-lg">
                {chat.avatar || '💬'}
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-sm">{chat.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {chat.lastMessage?.content || 'No messages yet'}
                </p>
              </div>
              {chat.unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-indigo-500 text-white rounded-full text-xs">
                  {chat.unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 bg-[#12121a] border border-[#2d2d3d] rounded-2xl overflow-hidden flex flex-col">
        {activeChatData ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-[#2d2d3d] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-lg">
                  {activeChatData.avatar || '💬'}
                </div>
                <div>
                  <p className="font-semibold">{activeChatData.name}</p>
                  <p className="text-xs text-gray-500">
                    {activeChatData.participants?.length || 0} participants
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="p-2 text-gray-400 hover:text-white transition-colors">
                  📞
                </button>
                <button className="p-2 text-gray-400 hover:text-white transition-colors">
                  ⚙️
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <p className="text-4xl mb-4">💬</p>
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <MessageBubble
                    key={msg._id || index}
                    message={msg}
                    isOwn={msg.sender?.type === 'user'}
                    agents={agents}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-[#2d2d3d]">
              <form onSubmit={sendMessage} className="flex gap-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message... Use @ to mention agents"
                    className="w-full px-4 py-3 bg-[#0a0a0f] border border-[#2d2d3d] rounded-xl text-white focus:outline-none focus:border-indigo-500"
                  />
                  {newMessage.includes('@') && (
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-[#1e1e2e] border border-[#2d2d3d] rounded-xl shadow-xl overflow-hidden">
                      {agents.map((agent) => (
                        <button
                          key={agent._id}
                          type="button"
                          onClick={() => {
                            setNewMessage(newMessage.replace(/@\w*$/, `@${agent.name} `));
                          }}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-[#2d2d3d] transition-colors"
                        >
                          <span className="text-lg">{agent.avatar || '🤖'}</span>
                          <span className="text-sm">{agent.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
                >
                  Send
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="text-6xl mb-4">👋</p>
              <p className="text-xl">Select a chat to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MessageBubble = ({ message, isOwn, agents }) => {
  const isAgent = message.sender?.type === 'agent';
  const isSystem = message.sender?.type === 'system';
  
  if (isSystem) {
    return (
      <div className="flex justify-center">
        <span className="px-4 py-1 bg-[#2d2d3d] rounded-full text-xs text-gray-500">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-lg flex-shrink-0">
        {message.sender?.avatar || (isOwn ? '👤' : '🤖')}
      </div>
      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">
            {message.sender?.name || 'Unknown'}
          </span>
          <span className="text-xs text-gray-500">
            {format(new Date(message.createdAt), 'h:mm a')}
          </span>
        </div>
        <div
          className={`px-4 py-2 rounded-2xl ${
            isOwn
              ? 'bg-indigo-500 text-white rounded-br-md'
              : 'bg-[#2d2d3d] text-gray-200 rounded-bl-md'
          }`}
        >
          <p className="text-sm">{message.content}</p>
        </div>
        {message.metadata?.tokens > 0 && (
          <span className="text-xs text-gray-500 mt-1">
            {message.metadata.tokens} tokens
          </span>
        )}
      </div>
    </div>
  );
};

export default Chat;
